/**
 * The tutor's progress chart, as data: what each point is, and which points a
 * line may join. Pure, and separate from the SVG, so the rules below are
 * tested as rules rather than as pixels.
 *
 * ── What the API sends ───────────────────────────────────────────────────
 *
 * `subjectProgress` in the domain returns, per subject, every submitted
 * attempt oldest first (its own percentage, its practice, and its number among
 * this student's attempts at that practice) and one summary per UTC day: the
 * mean of that day's attempt percentages in the subject, and how many attempts
 * it averages. Nothing here recomputes a score.
 *
 * ── Attempts: one point per attempt, lines only within a practice ────────
 *
 * Every attempt is a point on one ordinal axis, oldest first, across every
 * subject shown: the order is the information, and four tries twelve minutes
 * apart would share a pixel on a time scale that also spans weeks.
 *
 * A line joins attempts **at the same practice** and nothing else. The chart
 * used to join every attempt in a subject, so Fractions at 90 followed by
 * Geometry at 50 drew a fall — when the second number is a different, perhaps
 * harder, task and says nothing about the first. Retakes of one practice are
 * the one sequence whose numbers are comparable, so they are the one sequence
 * drawn as a line; a practice attempted once is a point on its own. A retake
 * sequence interleaved with other practices still joins, across them, because
 * it is still one task tried again.
 *
 * ── Daily: a subject's mean for the day ──────────────────────────────────
 *
 * The server's per-subject, per-UTC-day mean, drawn on a time axis and joined
 * per subject. It is a summary by definition — it averages across whatever
 * practices the student did in that subject that day — and every place that
 * shows it says so, with the attempt and practice counts behind it.
 */

export type ProgressView = 'attempts' | 'daily'

/** One submitted attempt, as the API sends it. */
export interface AttemptInput {
  attemptId: string
  practiceId: string
  practiceTitle: string
  submittedAt: string
  percent: number
  attemptNumber: number
}

/** One day's summary, as the API sends it. `date` is midnight UTC. */
export interface DayInput {
  date: string
  percent: number
  attemptCount: number
}

export interface SubjectInput {
  subjectId: string
  subjectName: string
  /**
   * The subject's place among every subject the student has, so its colour,
   * dash and marker stay the same when a filter hides the others.
   */
  styleIndex: number
  attempts: readonly AttemptInput[]
  points: readonly DayInput[]
}

interface PointBase {
  key: string
  subjectId: string
  subjectName: string
  styleIndex: number
  percent: number
}

export interface AttemptPoint extends PointBase {
  kind: 'attempt'
  /** Position on the ordinal axis, 0 for the oldest attempt shown. */
  index: number
  attemptId: string
  practiceId: string
  practiceTitle: string
  attemptNumber: number
  submittedAt: string
}

export interface DayPoint extends PointBase {
  kind: 'day'
  /** Midnight UTC. */
  date: string
  attemptCount: number
  /** How many different practices that day's mean spans. */
  practiceCount: number
}

export type ChartPoint = AttemptPoint | DayPoint

/** A run of points one line joins, in order. Never fewer than two. */
export interface ChartLine {
  key: string
  subjectId: string
  styleIndex: number
  pointKeys: string[]
}

export interface ChartModel {
  view: ProgressView
  /** Every point, in axis order: oldest attempt first, or earliest day first. */
  points: ChartPoint[]
  lines: ChartLine[]
  /** The subjects that have at least one point in this view, in their given order. */
  subjects: SubjectInput[]
}

/** Oldest first; the id breaks a tie, so the order is total. */
function bySubmission(a: AttemptInput, b: AttemptInput): number {
  return (
    Date.parse(a.submittedAt) - Date.parse(b.submittedAt) || a.attemptId.localeCompare(b.attemptId)
  )
}

export function attemptModel(series: readonly SubjectInput[]): ChartModel {
  const subjects = series.filter((subject) => subject.attempts.length > 0)
  const points: AttemptPoint[] = subjects
    .flatMap((subject) => subject.attempts.map((attempt) => ({ subject, attempt })))
    .sort((a, b) => bySubmission(a.attempt, b.attempt))
    .map(({ subject, attempt }, index) => ({
      kind: 'attempt',
      key: `attempt-${attempt.attemptId}`,
      index,
      subjectId: subject.subjectId,
      subjectName: subject.subjectName,
      styleIndex: subject.styleIndex,
      percent: attempt.percent,
      attemptId: attempt.attemptId,
      practiceId: attempt.practiceId,
      practiceTitle: attempt.practiceTitle,
      attemptNumber: attempt.attemptNumber,
      submittedAt: attempt.submittedAt,
    }))

  // One line per practice tried more than once. A practice belongs to one
  // subject, but the key carries both so nothing could ever join across two.
  const byPractice = new Map<string, AttemptPoint[]>()
  for (const point of points) {
    const key = `${point.subjectId}:${point.practiceId}`
    byPractice.set(key, [...(byPractice.get(key) ?? []), point])
  }
  const lines: ChartLine[] = [...byPractice]
    .filter(([, run]) => run.length > 1)
    .map(([key, run]) => ({
      key: `practice-${key}`,
      subjectId: run[0]!.subjectId,
      styleIndex: run[0]!.styleIndex,
      pointKeys: run.map((point) => point.key),
    }))

  return { view: 'attempts', points, lines, subjects }
}

/** `YYYY-MM-DD` in UTC: the day a daily point, and an attempt, belong to. */
function utcDay(iso: string): string {
  return new Date(iso).toISOString().slice(0, 10)
}

export function dailyModel(series: readonly SubjectInput[]): ChartModel {
  const subjects = series.filter((subject) => subject.points.length > 0)
  const points: DayPoint[] = subjects
    .flatMap((subject) =>
      subject.points.map((day) => {
        const practices = new Set(
          subject.attempts
            .filter((attempt) => utcDay(attempt.submittedAt) === utcDay(day.date))
            .map((attempt) => attempt.practiceId),
        )
        return {
          kind: 'day' as const,
          key: `${subject.subjectId}-${day.date}`,
          subjectId: subject.subjectId,
          subjectName: subject.subjectName,
          styleIndex: subject.styleIndex,
          percent: day.percent,
          date: day.date,
          attemptCount: day.attemptCount,
          // At least one: a day point exists only because something was submitted.
          practiceCount: Math.max(1, practices.size),
        }
      }),
    )
    .sort(
      (a, b) => Date.parse(a.date) - Date.parse(b.date) || a.subjectId.localeCompare(b.subjectId),
    )

  const lines: ChartLine[] = subjects.flatMap((subject) => {
    const own = points.filter((point) => point.subjectId === subject.subjectId)
    return own.length > 1
      ? [
          {
            key: `subject-${subject.subjectId}`,
            subjectId: subject.subjectId,
            styleIndex: subject.styleIndex,
            pointKeys: own.map((point) => point.key),
          },
        ]
      : []
  })

  return { view: 'daily', points, lines, subjects }
}

export function chartModel(series: readonly SubjectInput[], view: ProgressView): ChartModel {
  return view === 'attempts' ? attemptModel(series) : dailyModel(series)
}

/**
 * How many subjects one chart draws at once. Past six, lines stop being
 * readable and the palette runs out of colours it can tell apart; a tutor
 * with a student in more subjects than that sees the six most recently
 * practised, and picks any other from the subject filter.
 */
export const MAX_SUBJECTS_AT_ONCE = 6

/** The subjects "every subject" shows: all of them, or the most recently practised six. */
export function defaultScope(series: readonly SubjectInput[]): SubjectInput[] {
  if (series.length <= MAX_SUBJECTS_AT_ONCE) return [...series]
  const latest = (subject: SubjectInput) =>
    Math.max(0, ...subject.attempts.map((attempt) => Date.parse(attempt.submittedAt)))
  const recent = new Set(
    [...series]
      .sort((a, b) => latest(b) - latest(a) || a.subjectId.localeCompare(b.subjectId))
      .slice(0, MAX_SUBJECTS_AT_ONCE)
      .map((subject) => subject.subjectId),
  )
  // Back in their own order, so the legend does not reshuffle.
  return series.filter((subject) => recent.has(subject.subjectId))
}
